/**
 * LineChart and AreaChart: trends over time, 1-4 series. Crosshair + one tooltip listing every series at the hovered
 * bucket; a null point is a gap, never a dip to zero. Keyboard: the plot is one tab stop, arrows move the crosshair
 * (left/right) and pick the series (up/down), Enter drills. Owner: slice web-charts (#1115).
 */
import * as React from 'react'
import { XLabels, YGrid } from './axes'
import { HEIGHT, INK, MARGIN, MARK } from './chart-theme'
import { ChartTooltip, tooltipText } from './chart-tooltip'
import { axisFormatter, formatValue, xLabel } from './format'
import { labelEvery, linear, niceTicks, spreadLabels, useWidth } from './geometry'
import { buildModel, type ChartProps, type PlotSeries } from './model'

export function LineChart(props: ChartProps) {
  return <XYChart {...props} area={false} />
}

export function AreaChart(props: ChartProps) {
  return <XYChart {...props} area />
}

function segments(
  values: (number | null)[],
  xAt: (i: number) => number,
  y: (v: number) => number,
): { path: string; single: number | null }[] {
  const out: { path: string; single: number | null }[] = []
  let cur: string[] = []
  let start = -1
  const flush = () => {
    if (cur.length === 1) out.push({ path: '', single: start })
    else if (cur.length > 1) out.push({ path: cur.join(''), single: null })
    cur = []
  }
  values.forEach((v, i) => {
    if (v === null) {
      flush()
      return
    }
    if (cur.length === 0) start = i
    cur.push(`${cur.length === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${y(v).toFixed(1)}`)
  })
  flush()
  return out
}

export function summarise(kind: string, title: string, plotted: PlotSeries[], first: string, last: string, count: number): string {
  const names = plotted.map((p) => p.label).join(', ')
  return `${title}. ${kind} of ${names || 'no series'}, ${count} points from ${first} to ${last}. The table view lists every value.`
}

function XYChart({ chart, onPointClick, canSee, area }: ChartProps & { area: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const model = React.useMemo(() => buildModel(chart, canSee), [chart, canSee])
  const [hover, setHover] = React.useState<{ i: number; s: number } | null>(null)
  const { xs, plotted } = model
  const height = HEIGHT.line
  const direct = plotted.length >= 2 && plotted.length <= 4 && width >= 360
  const left = MARGIN.left
  const right = width - (direct ? 96 : MARGIN.right)
  const top = MARGIN.top
  const bottom = height - MARGIN.bottom
  const ticks = niceTicks(model.min, model.max)
  const y = linear(ticks[0] ?? 0, ticks[ticks.length - 1] ?? 1, bottom, top)
  const pw = Math.max(1, right - left)
  const xAt = (i: number) => (xs.length <= 1 ? left + pw / 2 : left + (i * pw) / (xs.length - 1))
  const fmt = axisFormatter(chart.unit, Math.max(Math.abs(model.min), Math.abs(model.max)))
  const labels = xs.map((x) => xLabel(chart, x))
  const every = labelEvery(xs.length, pw)
  const baseY = y(Math.max(ticks[0] ?? 0, 0))

  const nearestSeries = (i: number, py: number | null): number => {
    let best = 0
    let bestD = Number.POSITIVE_INFINITY
    plotted.forEach((p, s) => {
      const v = p.values[i]
      if (v === null || v === undefined) return
      const d = py === null ? s : Math.abs(y(v) - py)
      if (d < bestD) {
        bestD = d
        best = s
      }
    })
    return best
  }

  /** The bucket + series under a pointer position (client coordinates), so a tap drills what it touched. */
  const pointAt = (el: HTMLElement, clientX: number, clientY: number): { i: number; s: number } => {
    const box = el.getBoundingClientRect()
    const scale = box.width > 0 ? pw / box.width : 1
    const px = left + (clientX - box.left) * scale
    const py = top + (clientY - box.top) * scale
    const i = xs.length <= 1 ? 0 : Math.max(0, Math.min(xs.length - 1, Math.round(((px - left) / pw) * (xs.length - 1))))
    return { i, s: nearestSeries(i, py) }
  }
  const fromPointer = (e: React.PointerEvent<HTMLButtonElement>) => setHover(pointAt(e.currentTarget, e.clientX, e.clientY))

  // A pointer click (mouse or touch tap: detail > 0) drills the point under the pointer, never the focus default; a
  // keyboard Enter/Space click (detail 0) drills the crosshair the arrows moved.
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0) {
      const h = pointAt(e.currentTarget, e.clientX, e.clientY)
      setHover(h)
      drill(h)
      return
    }
    drill(hover)
  }

  const drill = (h: { i: number; s: number } | null) => {
    if (!h || !onPointClick) return
    const p = plotted[h.s]
    const x = xs[h.i]
    if (!p || x === undefined || p.values[h.i] === null) return
    onPointClick({ x, series: p.key })
  }

  const onKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (xs.length === 0 || plotted.length === 0) return
    const h = hover ?? { i: 0, s: 0 }
    let next = h
    if (e.key === 'ArrowRight') next = { ...h, i: Math.min(xs.length - 1, h.i + 1) }
    else if (e.key === 'ArrowLeft') next = { ...h, i: Math.max(0, h.i - 1) }
    else if (e.key === 'ArrowDown') next = { ...h, s: (h.s + 1) % plotted.length }
    else if (e.key === 'ArrowUp') next = { ...h, s: (h.s - 1 + plotted.length) % plotted.length }
    else if (e.key === 'Home') next = { ...h, i: 0 }
    else if (e.key === 'End') next = { ...h, i: xs.length - 1 }
    else return
    e.preventDefault()
    setHover(next)
  }

  // Direct end labels at each series' last real point, spread so they never collide.
  const ends = plotted.map((p) => {
    for (let i = p.values.length - 1; i >= 0; i--) {
      const v = p.values[i]
      if (v !== null && v !== undefined) return y(v)
    }
    return null
  })
  const spread = spreadLabels(
    ends.map((e) => e ?? bottom),
    13,
    top,
    bottom,
  )

  const hoverRows =
    hover === null
      ? []
      : plotted.map((p) => ({ key: p.key, label: p.label, color: p.color, value: p.values[hover.i] ?? null, n: p.ns[hover.i] ?? null }))
  const hoverTitle = hover === null ? '' : xLabel(chart, xs[hover.i] ?? '', true)
  const kindWord = area ? 'Area chart' : 'Line chart'

  return (
    <div ref={ref} className="reports-root relative w-full" data-chart-kind={area ? 'area' : 'line'}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={summarise(kindWord, chart.title, plotted, labels[0] ?? '', labels[labels.length - 1] ?? '', xs.length)}
        className="block overflow-visible"
      >
        <YGrid ticks={ticks} y={y} left={left} right={right} format={fmt} />
        <XLabels labels={labels} xAt={xAt} y={height - 8} every={every} />
        {plotted.map((p) => {
          const segs = segments(p.values, xAt, y)
          return (
            <g key={p.key} data-series={p.key}>
              {segs.map((s, k) =>
                s.single !== null ? (
                  <circle
                    // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional
                    key={k}
                    data-line-point
                    cx={xAt(s.single)}
                    cy={y(p.values[s.single] ?? 0)}
                    r={3}
                    fill={p.color}
                  />
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional
                  <g key={k}>
                    {area ? (
                      <path
                        data-area-segment
                        d={`${s.path}V${baseY}H${s.path.match(/^M([\d.]+)/)?.[1] ?? left}Z`}
                        fill={p.color}
                        fillOpacity={MARK.areaOpacity}
                        stroke="none"
                      />
                    ) : null}
                    <path
                      data-line-segment
                      d={s.path}
                      fill="none"
                      stroke={p.color}
                      strokeWidth={MARK.line}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  </g>
                ),
              )}
            </g>
          )
        })}
        {direct
          ? plotted.map((p, k) =>
              ends[k] === null ? null : (
                <g key={p.key}>
                  <line
                    x1={right + 6}
                    x2={right + 14}
                    y1={spread[k]}
                    y2={spread[k]}
                    stroke={p.color}
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                  <text x={right + 18} y={spread[k]} dy="0.32em" fontSize={11} fill={INK.primary}>
                    {p.label.length > 12 ? `${p.label.slice(0, 11)}…` : p.label}
                  </text>
                </g>
              ),
            )
          : null}
        {hover !== null ? (
          <g pointerEvents="none">
            <line x1={xAt(hover.i)} x2={xAt(hover.i)} y1={top} y2={bottom} stroke="var(--color-border-strong)" strokeWidth={1} />
            {plotted.map((p, s) => {
              const v = p.values[hover.i]
              if (v === null || v === undefined) return null
              return (
                <circle
                  key={p.key}
                  cx={xAt(hover.i)}
                  cy={y(v)}
                  r={MARK.marker / 2 + (s === hover.s ? 1 : 0)}
                  fill={p.color}
                  stroke={INK.surface}
                  strokeWidth={MARK.ring}
                />
              )
            })}
          </g>
        ) : null}
      </svg>
      <button
        type="button"
        data-plot
        aria-label={
          hover === null
            ? `${chart.title}: use the arrow keys to read values, Enter to open the cases`
            : tooltipText(hoverTitle, hoverRows, chart.unit)
        }
        aria-disabled={onPointClick ? undefined : true}
        disabled={xs.length === 0 || plotted.length === 0}
        className="absolute block rounded-sm bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
        style={{ left, top, width: pw, height: Math.max(1, bottom - top), cursor: onPointClick ? 'pointer' : 'default' }}
        onKeyDown={onKey}
        onFocus={() => setHover((h) => h ?? { i: xs.length - 1, s: 0 })}
        onBlur={() => setHover(null)}
        onPointerDown={fromPointer}
        onPointerMove={fromPointer}
        onPointerLeave={() => setHover(null)}
        onClick={onClick}
      />
      {hover !== null ? (
        <ChartTooltip x={xAt(hover.i)} y={top} containerWidth={width} title={hoverTitle} rows={hoverRows} unit={chart.unit} />
      ) : null}
      <span className="sr-only" aria-live="polite">
        {hover !== null
          ? tooltipText(
              hoverTitle,
              hoverRows.filter((_, s) => s === hover.s),
              chart.unit,
            )
          : ''}
      </span>
      <ValueRangeNote unit={chart.unit} min={model.min} max={model.max} hasData={model.hasData} />
    </div>
  )
}

function ValueRangeNote({ unit, min, max, hasData }: { unit: ChartProps['chart']['unit']; min: number; max: number; hasData: boolean }) {
  if (!hasData) return null
  return (
    <span className="sr-only">
      Values range from {formatValue(unit, min)} to {formatValue(unit, max)}.
    </span>
  )
}
